import { useEffect, useState, useCallback } from "react";
import { useAuth, useEtiquetaCritico, useNorma } from "@/context/AuthContext";
import api, { descargarRecurso } from "@/lib/api";
import { ScopeNotice, useScopeGuard } from "@/components/common";
import { IAExplain } from "@/components/IAExplain";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Download, Search, FileText, AlertTriangle, ClipboardList, ShieldCheck, Printer } from "lucide-react";
import { resumenCobertura, COBERTURA_STYLE, estadoLabel, ESTADO } from "@/lib/cobertura";
import { verifyAttrs } from "@/verify/contract";
import { toast } from "sonner";

export default function Inspector() {
  const { user, scopeTenant, tenant } = useAuth();
  const krit = useEtiquetaCritico();
  const norma = useNorma();
  const needsScope = useScopeGuard(user, scopeTenant);
  const [cob, setCob] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [incs, setIncs] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    if (needsScope) return;
    const [c, g, i] = await Promise.all([api.get("/norma/cobertura"), api.get("/gaps"), api.get("/incidencias")]);
    setCob(c.data); setGaps(g.data); setIncs(i.data);
  }, [needsScope]);
  useEffect(() => { load(); }, [load]);

  const descargar = async (docId) => {
    try { await descargarRecurso("documento", docId); }
    catch { toast.error("No se pudo generar la descarga"); }
  };

  if (needsScope) return <ScopeNotice />;
  if (!cob) return <div className="text-[#0E2A47]/60">Cargando modo inspector…</div>;

  const filtro = q.trim().toLowerCase();
  const matchReq = (r) => !filtro || r.codigo.toLowerCase().includes(filtro) || r.texto.toLowerCase().includes(filtro);
  const resumen = resumenCobertura(cob);
  const reservas = [];
  cob.capitulos.forEach((cap) => cap.requisitos.forEach((r) => {
    if (r.estado_cobertura === ESTADO.CON_RESERVAS && matchReq(r)) reservas.push({ ...r, capitulo: cap.capitulo });
  }));

  return (
    <div data-testid="inspector-page" className="print:bg-white">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5625] mb-1 flex items-center gap-2"><ShieldCheck size={14} /> Modo Inspector · solo lectura</div>
          <h1 className="text-3xl font-black tracking-tighter text-[#0E2A47]">Dossier de Auditoría {norma.nombre}</h1>
          <p className="text-sm text-[#0E2A47]/50 mt-1">{tenant?.nombre || "Tenant"}</p>
        </div>
        <button onClick={() => window.print()} data-testid="inspector-print" className="print:hidden flex items-center gap-2 text-sm text-[#0E2A47]/70 hover:text-[#FF5625]"><Printer size={16} /> Imprimir</button>
      </div>

      <div data-testid="inspector-cobertura-banner" {...verifyAttrs("CoberturaRequisitos", resumen)}
        className="mb-6 rounded-xl border border-[#0E2A47]/10 bg-[#0E2A47] text-white px-5 py-4 flex items-center justify-between gap-4 print:border print:bg-white print:text-[#0E2A47]">
        <div className="flex items-center gap-3">
          <ShieldCheck size={22} className="text-[#FF5625]" />
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-white/60 print:text-[#0E2A47]/60">Cobertura verificada</div>
            <div className="text-sm text-white/80 print:text-[#0E2A47]/80"><b>{cob.cubiertos}</b> de {cob.total} cubiertos · {cob.con_reservas} con reservas · {cob.sin_evidencia} sin evidencia</div>
          </div>
        </div>
        <div className="text-3xl font-black tracking-tighter" data-testid="inspector-cobertura-pct">{cob.cobertura_pct}%</div>
      </div>

      {reservas.length > 0 && (
        <div className="mb-6 rounded-xl border border-[#F59E0B]/50 bg-[#FFFBEB] p-4 print:break-inside-avoid" data-testid="inspector-reservas-section">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className="text-[#B45309]" />
            <span className="font-bold text-[#B45309]">Requisitos con reservas ({reservas.length}) · no cuentan como cubiertos</span>
          </div>
          <div className="space-y-2">
            {reservas.map((r) => (
              <div key={r.codigo} className="border-t border-[#F59E0B]/20 pt-2" data-testid={`insp-reserva-${r.codigo}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-[#FF5625]">{r.codigo}</span>
                  {r.ko && <Badge className="bg-[#0E2A47] hover:bg-[#0E2A47] text-[10px]">{krit}</Badge>}
                  <span className="text-[11px] font-semibold" style={{ color: COBERTURA_STYLE.con_reservas.color }}>{estadoLabel(r)}</span>
                  <span className="text-sm text-[#0E2A47]/80">{r.texto}</span>
                </div>
                {(r.motivo_reservas?.length > 0) && (
                  <ul className="list-disc pl-5 mt-1">
                    {r.motivo_reservas.map((m, i) => <li key={i} className="text-xs text-[#0E2A47]/70">{m}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="relative max-w-lg mb-6 print:hidden">
        <Search size={18} className="absolute left-3 top-3.5 text-[#0E2A47]/40" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="inspector-search" placeholder="Buscar requisito por número o texto…" className="pl-10 h-12 text-base" />
      </div>

      <Accordion type="multiple" className="space-y-3">
        {cob.capitulos.map((cap) => {
          const reqs = cap.requisitos.filter((r) => matchReq(r) && r.estado_cobertura !== ESTADO.CON_RESERVAS);
          if (reqs.length === 0) return null;
          return (
            <AccordionItem key={cap.capitulo} value={cap.capitulo} className="border border-[#E2E8F0] rounded-lg bg-white px-4" data-testid={`insp-cap-${cap.capitulo}`}>
              <AccordionTrigger className="hover:no-underline">
                <span className="font-semibold text-[#0E2A47] text-left"><span className="font-mono text-[#FF5625] mr-2">Cap. {cap.capitulo}</span>{cap.titulo}</span>
              </AccordionTrigger>
              <AccordionContent>
                {reqs.map((r) => {
                  const rGaps = gaps.filter((g) => g.requisito === r.codigo && g.estado !== "cerrado");
                  const rIncs = incs.filter((i) => i.requisito_id === r.codigo);
                  return (
                    <div key={r.codigo} className="py-3 border-t border-[#F1F5F9]" data-testid={`insp-req-${r.codigo}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-[#FF5625]">{r.codigo}</span>
                        {r.ko && <Badge className="bg-[#0E2A47] hover:bg-[#0E2A47] text-[10px]">{krit}</Badge>}
                        <span className="text-sm text-[#0E2A47]/85">{r.texto}</span>
                        <IAExplain numero={r.codigo} texto={r.texto} />
                      </div>
                      <div className="ml-4 mt-2 space-y-1">
                        {r.documentos.map((d) => (
                          <button key={d.id} onClick={() => descargar(d.id)} data-testid={`insp-doc-${d.id}`}
                            className="flex items-center gap-2 text-sm text-[#0E2A47]/70 hover:text-[#FF5625] print:hidden">
                            <Download size={13} /> <FileText size={13} /> {d.titulo}
                          </button>
                        ))}
                        {r.documentos.length === 0 && <div className="text-xs text-[#0E2A47]/40">Sin evidencia documental vigente.</div>}
                        {rGaps.map((g) => <div key={g.id} className="flex items-center gap-2 text-xs text-[#FF5625]"><AlertTriangle size={12} /> Gap {g.tipo === "KO" ? krit : g.tipo}: {g.descripcion?.slice(0, 60)}</div>)}
                        {rIncs.map((i) => <div key={i.id} className="flex items-center gap-2 text-xs text-[#0E2A47]/60"><ClipboardList size={12} /> Incidencia ({i.estado}): {i.descripcion?.slice(0, 60)}</div>)}
                      </div>
                    </div>
                  );
                })}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
